/**
 * Spring Theme Bloom Engine
 *
 * Pure functions that compute the user's current bloom level from a small set
 * of inputs. Two contributions stack:
 *
 *   floor_today    — interpolated by date alone, so every enabled user reaches
 *                    1.0 by FLOOR_MAX_DATE regardless of activity.
 *   activity_lift  — capped contribution from recognition activity since the
 *                    user enabled the theme.
 *
 *   target_bloom   = clamp(floor_today + activity_lift, 0, 1)
 *
 * Bloom only ever increases — `applyNeverRegress` enforces that callers max
 * the new target against the previously stored value.
 *
 * All functions here are deterministic and side-effect free so they can be
 * unit-tested without mocking storage or the clock.
 */

/** Spring theme rolled out to all users. */
export const THEME_START_DATE = '2026-04-22';

/**
 * Date the bloom floor reaches 1.0. Set a few days before the spring theme
 * actually ends (May 8) so every enabled user lands at full bloom before the
 * season closes, even if they never recognized anyone.
 */
export const FLOOR_MAX_DATE = '2026-05-06';

/**
 * Bloom contribution per recognition (given + received). Each recognition
 * adds this much lift on top of the date-based floor; the total lift is then
 * clamped at DAILY_CAP, so 2 recognitions saturate the lift contribution and
 * the rest of the path to full bloom is carried by the floor.
 */
export const LIFT_FACTOR = 0.1;

/**
 * Maximum activity_lift contribution in a single bloom recompute. With the
 * current LIFT_FACTOR (0.1), this caps the activity contribution at the
 * equivalent of ~2 recognitions — enough to feel rewarding for active users
 * without letting a burst spike past the floor.
 */
export const DAILY_CAP = 0.2;

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/**
 * Parse a YYYY-MM-DD local-date string to a Date at local midnight. Returns
 * `null` for any malformed input so callers can fail safely.
 */
const parseLocalDate = (date: string): Date | null => {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
	if (!match) return null;
	const [, yyyy, mm, dd] = match;
	const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
	return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Format a Date as a local YYYY-MM-DD string. Avoids `toISOString()` because
 * that would shift the date for users east of UTC.
 */
export const formatLocalDate = (d: Date): string => {
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
};

export const todayLocalDate = (): string => formatLocalDate(new Date());

/**
 * Clamp `enableDate` so it never sits in the future (clock skew). Returns the
 * earlier of `enableDate` and `today`. If `enableDate` is malformed, returns
 * today so the user still gets a sensible floor.
 */
export const normalizeEnableDate = (enableDate: string, today = todayLocalDate()): string => {
	const enabled = parseLocalDate(enableDate);
	const now = parseLocalDate(today);
	if (!enabled || !now) return today;
	return enabled.getTime() > now.getTime() ? today : enableDate;
};

/**
 * Compute the bloom floor for `today` based on `enableDate`.
 *
 * Linearly interpolates from 0 on enableDate to 1 on FLOOR_MAX_DATE. Returns 0
 * before enableDate and 1 on or after FLOOR_MAX_DATE.
 *
 * The FLOOR_MAX_DATE check runs first so users opting in on or after May 6
 * land at full bloom immediately — without this, a same-day enable on
 * FLOOR_MAX_DATE would match the `<= enabled` branch and return 0.
 */
export const computeFloor = (enableDate: string, today = todayLocalDate()): number => {
	const enabled = parseLocalDate(normalizeEnableDate(enableDate, today));
	const now = parseLocalDate(today);
	const max = parseLocalDate(FLOOR_MAX_DATE);
	if (!enabled || !now || !max) return 0;

	if (now.getTime() >= max.getTime()) return 1;
	if (now.getTime() <= enabled.getTime()) return 0;

	const total = max.getTime() - enabled.getTime();
	const elapsed = now.getTime() - enabled.getTime();
	return clamp01(elapsed / total);
};

/**
 * Compute the activity-driven lift. Each recognition contributes LIFT_FACTOR
 * up to DAILY_CAP per recompute pass.
 */
export const computeActivityLift = (totalCount: number): number => {
	if (!Number.isFinite(totalCount) || totalCount <= 0) return 0;
	return Math.min(totalCount * LIFT_FACTOR, DAILY_CAP);
};

/**
 * Combine floor + lift into a target bloom value in [0, 1].
 */
export const computeTargetBloom = (totalCount: number, enableDate: string, today = todayLocalDate()): number => {
	return clamp01(computeFloor(enableDate, today) + computeActivityLift(totalCount));
};

/**
 * Bloom only ever increases — the cached value wins if the new target is
 * lower. Exposed as its own function so callers can assert the guarantee
 * loudly rather than relying on it being baked into a larger pipeline.
 */
export const applyNeverRegress = (cached: number, target: number): number => Math.max(cached, target);
